import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FadeInSection from '../../../components/base/FadeInSection';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import DiaryForm from './DiaryForm';
import { toggleDiaryLike } from '../../../hooks/useNotifications';

// ... existing code ...

function MaterialUrlCard({ url }: { url: string }) {
  let domain = '';
  let displayUrl = url;
  try {
    const parsed = new URL(url);
    domain = parsed.hostname;
    displayUrl = parsed.hostname + parsed.pathname.slice(0, 40) + (parsed.pathname.length > 40 ? '...' : '');
  } catch {
    displayUrl = url.length > 60 ? url.slice(0, 60) + '...' : url;
  }
  const faviconUrl = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=32` : null;
  const getSiteName = (d: string) => {
    if (d.includes('amazon')) return 'Amazon';
    if (d.includes('rakuten')) return '楽天市場';
    if (d.includes('yahoo')) return 'Yahoo!ショッピング';
    if (d.includes('mercari')) return 'メルカリ';
    if (d.includes('yodobashi')) return 'ヨドバシカメラ';
    if (d.includes('bic')) return 'ビックカメラ';
    return domain || '外部サイト';
  };
  return (
    <div className="rounded-xl border border-amber-200 dark:border-amber-700 overflow-hidden bg-amber-50 dark:bg-amber-900/20">
      <div className="flex items-center gap-3 px-4 py-3">
        {faviconUrl && <img src={faviconUrl} alt={domain} className="w-6 h-6 rounded flex-shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-0.5">{getSiteName(domain)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{displayUrl}</p>
        </div>
        <a href={url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap">
          <i className="ri-external-link-line text-sm"></i>商品を見る
        </a>
      </div>
      <div className="px-4 pb-2.5">
        <p className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
          <i className="ri-information-line text-xs"></i>※アフィリエイトリンクが含まれる場合があります
        </p>
      </div>
    </div>
  );
}

interface DiaryEntry {
  id: string; user_id: string; title: string; subject: string; content: string;
  study_hours: number | null; is_public: boolean; tags: string[]; mood?: string;
  material_url?: string; created_at: string; updated_at: string;
  user_profiles?: { username: string; avatar_url?: string };
}

export default function DiaryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [entry, setEntry] = useState<DiaryEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [prevEntry, setPrevEntry] = useState<{ id: string; title: string } | null>(null);
  const [nextEntry, setNextEntry] = useState<{ id: string; title: string } | null>(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likingInProgress, setLikingInProgress] = useState(false);

  const moods = [
    { value: 'great', emoji: '😊', label: '良い' }, { value: 'normal', emoji: '😐', label: '普通' },
    { value: 'down', emoji: '😔', label: '落ち込み' }, { value: 'unmotivated', emoji: '😤', label: '無気力' },
    { value: 'motivated', emoji: '💪', label: 'やる気満々' },
  ];
  const getMoodData = (v?: string) => v ? moods.find(m => m.value === v) || null : null;

  useEffect(() => { if (!authLoading) fetchEntry(); }, [id, authLoading]);
  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 2500); return () => clearTimeout(t); } }, [toast]);

  const fetchEntry = async () => {
    if (!id) { navigate('/diary'); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.from('diary_entries').select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      if (!data) { setToast({ message: '日記が見つかりませんでした', type: 'error' }); setTimeout(() => navigate('/diary'), 1500); return; }
      if (!data.is_public && (!user || data.user_id !== user.id)) { setToast({ message: 'この日記は非公開です', type: 'error' }); setTimeout(() => navigate('/diary'), 1500); return; }
      const { data: profile } = await supabase.from('user_profiles').select('username, avatar_url').eq('user_id', data.user_id).maybeSingle();
      setEntry({ ...data, user_profiles: profile || undefined });
      await fetchAdjacentEntries(data.created_at, data.user_id, data.is_public);
      // いいね状態取得
      const { count: cnt } = await supabase.from('diary_likes').select('*', { count: 'exact', head: true }).eq('diary_entry_id', id);
      setLikeCount(cnt || 0);
      if (user) {
        const { data: myLike } = await supabase.from('diary_likes').select('id').eq('diary_entry_id', id).eq('user_id', user.id).maybeSingle();
        setLiked(!!myLike);
      }
    } catch (error) {
      console.error('Error fetching diary entry:', error);
      setToast({ message: '日記の読み込みに失敗しました', type: 'error' });
      setTimeout(() => navigate('/diary'), 1500);
    } finally { setLoading(false); }
  };

  const fetchAdjacentEntries = async (currentDate: string, userId: string, isPublic: boolean) => {
    try {
      let prevQ = supabase.from('diary_entries').select('id, title').lt('created_at', currentDate).order('created_at', { ascending: false }).limit(1);
      let nextQ = supabase.from('diary_entries').select('id, title').gt('created_at', currentDate).order('created_at', { ascending: true }).limit(1);
      if (user && userId === user.id) { prevQ = prevQ.eq('user_id', user.id); nextQ = nextQ.eq('user_id', user.id); }
      else { prevQ = prevQ.eq('is_public', true); nextQ = nextQ.eq('is_public', true); }
      const [p, n] = await Promise.all([prevQ.maybeSingle(), nextQ.maybeSingle()]);
      setPrevEntry(p.data || null); setNextEntry(n.data || null);
    } catch (e) { console.error(e); }
  };

  const handleLike = async () => {
    if (!user || !entry || likingInProgress) return;
    setLikingInProgress(true);
    try {
      const result = await toggleDiaryLike(entry.id, entry.title, entry.user_id, user.id, '');
      setLiked(result.liked);
      setLikeCount(result.count);
    } catch (e) { console.error(e); } finally { setLikingInProgress(false); }
  };

  const handleDelete = async () => {
    if (!entry || !user || entry.user_id !== user.id) return;
    if (!confirm('この日記を削除してもよろしいですか？')) return;
    try {
      const { error } = await supabase.from('diary_entries').delete().eq('id', entry.id).eq('user_id', user.id);
      if (error) throw error;
      setToast({ message: '日記を削除しました', type: 'success' });
      setTimeout(() => navigate('/diary'), 1000);
    } catch { setToast({ message: '削除に失敗しました', type: 'error' }); }
  };

  const isMyEntry = entry && user && entry.user_id === user.id;

  if (authLoading || loading) {
    return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center"><div className="flex flex-col items-center gap-4"><div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div><p className="text-gray-600 dark:text-gray-400">読み込み中...</p></div></div>;
  }
  if (!entry) return null;

  const moodData = getMoodData(entry.mood);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Header />
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[60] animate-fade-in">
          <div className={`px-5 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white' : 'bg-red-500 text-white'}`}>
            <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-base`}></i>{toast.message}
          </div>
        </div>
      )}

      <section className="pt-24 pb-12 hero-gradient-animated hero-dark-force relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="hero-orb-1 absolute -top-20 -right-20 w-[420px] h-[420px] bg-white/10 rounded-full blur-3xl"></div>
          <div className="hero-orb-2 absolute -bottom-32 -left-20 w-[500px] h-[500px] bg-emerald-300/10 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative">
          <nav className="flex items-center gap-2 text-sm text-teal-200 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Top</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/diary" className="hover:text-white transition-colors">勉強日記</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white font-medium">日記詳細</span>
          </nav>
          <Link to="/diary" className="inline-flex items-center gap-2 text-teal-100 hover:text-white transition-colors mb-6 cursor-pointer">
            <i className="ri-arrow-left-line"></i><span className="text-sm font-medium">日記一覧に戻る</span>
          </Link>
        </div>
      </section>

      <FadeInSection>
        <section className="py-10 sm:py-14">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-teal-500 to-emerald-600 px-6 sm:px-8 py-6 sm:py-8">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      {entry.subject && <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/30">{entry.subject}</span>}
                      {moodData && <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/30 flex items-center gap-1.5"><span className="text-base">{moodData.emoji}</span><span>{moodData.label}</span></span>}
                      {!entry.is_public && <span className="px-3 py-1 bg-amber-500/30 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/30 flex items-center gap-1.5"><i className="ri-lock-line text-sm"></i>非公開</span>}
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">{entry.title}</h1>
                  </div>
                  {isMyEntry && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => setShowEditForm(true)} className="w-10 h-10 flex items-center justify-center bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-xl transition-colors cursor-pointer border border-white/30" title="編集"><i className="ri-edit-line text-lg text-white"></i></button>
                      <button onClick={handleDelete} className="w-10 h-10 flex items-center justify-center bg-red-500/30 backdrop-blur-sm hover:bg-red-500/50 rounded-xl transition-colors cursor-pointer border border-white/30" title="削除"><i className="ri-delete-bin-line text-lg text-white"></i></button>
                    </div>
                  )}
                </div>
                {entry.user_profiles && (
                  <div className="flex items-center gap-3 mb-4">
                    {entry.user_profiles.avatar_url ? <img src={entry.user_profiles.avatar_url} alt={entry.user_profiles.username} className="w-12 h-12 rounded-full object-cover border-2 border-white/30" /> : <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white text-lg font-semibold border-2 border-white/30">{entry.user_profiles.username.charAt(0).toUpperCase()}</div>}
                    <div>
                      <p className="text-white font-semibold">{entry.user_profiles.username}</p>
                      <p className="text-teal-100 text-sm">{new Date(entry.created_at).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-4 flex-wrap">
                  {entry.study_hours !== null && (
                    <div className="flex items-center gap-2 text-white">
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-xl border border-white/30"><i className="ri-time-line text-lg"></i></div>
                      <div><p className="text-xs text-teal-100">学習時間</p><p className="text-lg font-bold">{entry.study_hours}時間</p></div>
                    </div>
                  )}
                  {/* いいねボタン */}
                  <button
                    onClick={handleLike}
                    disabled={!user || likingInProgress}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all cursor-pointer disabled:opacity-50 ${liked ? 'bg-red-500/30 border-red-400/50 text-white' : 'bg-white/15 border-white/30 text-white hover:bg-white/25'}`}
                    title={user ? (liked ? 'いいねを取り消す' : 'いいねする') : 'ログインが必要です'}
                  >
                    <i className={`${liked ? 'ri-heart-fill text-red-300' : 'ri-heart-line'} text-lg`}></i>
                    <span className="text-sm font-semibold">{likeCount}</span>
                  </button>
                </div>
              </div>

              <div className="px-6 sm:px-8 py-6 sm:py-8">
                <div className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap text-base">{entry.content}</div>
                {entry.material_url && (
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2"><i className="ri-shopping-cart-line text-amber-600 dark:text-amber-400"></i>勉強中の教材</h3>
                    <MaterialUrlCard url={entry.material_url} />
                  </div>
                )}
              </div>

              {entry.tags && entry.tags.length > 0 && (
                <div className="px-6 sm:px-8 pb-6 sm:pb-8 border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2"><i className="ri-price-tag-3-line"></i>タグ</h3>
                  <div className="flex flex-wrap gap-2">
                    {entry.tags.map((tag, i) => (
                      <button key={i} onClick={() => navigate('/diary', { state: { filterTag: tag } })} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-sm rounded-full border border-teal-200 dark:border-teal-800 hover:bg-teal-100 dark:hover:bg-teal-900/50 transition-colors cursor-pointer">
                        <i className="ri-hashtag text-xs"></i>{tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="px-6 sm:px-8 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 flex-wrap gap-2">
                  <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>投稿日: {new Date(entry.created_at).toLocaleString('ja-JP')}</span>
                  {entry.updated_at !== entry.created_at && <span className="flex items-center gap-1.5"><i className="ri-edit-line"></i>更新日: {new Date(entry.updated_at).toLocaleString('ja-JP')}</span>}
                </div>
              </div>
            </div>

            {(prevEntry || nextEntry) && (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {prevEntry ? (
                  <Link to={`/diary/${prevEntry.id}`} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-lg group-hover:bg-teal-100 transition-colors flex-shrink-0"><i className="ri-arrow-left-line text-lg"></i></div>
                    <div className="flex-1 min-w-0"><p className="text-xs text-gray-500 dark:text-gray-400 mb-1">前の日記</p><p className="text-sm font-medium text-gray-800 dark:text-white truncate">{prevEntry.title}</p></div>
                  </Link>
                ) : <div></div>}
                {nextEntry && (
                  <Link to={`/diary/${nextEntry.id}`} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group">
                    <div className="flex-1 min-w-0 text-right"><p className="text-xs text-gray-500 dark:text-gray-400 mb-1">次の日記</p><p className="text-sm font-medium text-gray-800 dark:text-white truncate">{nextEntry.title}</p></div>
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-lg group-hover:bg-teal-100 transition-colors flex-shrink-0"><i className="ri-arrow-right-line text-lg"></i></div>
                  </Link>
                )}
              </div>
            )}

            <div className="mt-8 text-center">
              <Link to="/diary" className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer border border-gray-200 dark:border-gray-700 shadow-sm whitespace-nowrap">
                <i className="ri-arrow-left-line"></i>日記一覧に戻る
              </Link>
            </div>
          </div>
        </section>
      </FadeInSection>

      {showEditForm && user && entry && (
        <DiaryForm userId={user.id} entry={entry} onClose={() => setShowEditForm(false)} onSaved={() => { setShowEditForm(false); setToast({ message: '日記を更新しました', type: 'success' }); fetchEntry(); }} />
      )}
      <Footer />
    </div>
  );
}
